// Lazy-loads the background video in the "Featured collection with media" section.
//
// The section renders a lightweight poster <img> plus a <template> holding the
// real <video preload="none"> (no autoplay), so the browser never fetches the
// .mp4 at parse time. This module swaps the poster for the video via an
// IntersectionObserver once the section nears the viewport, and wires the
// floating play/pause button — which theme.js's own handler can't do, because
// the <video> is still inside the (inert) <template> when that handler runs at
// section load.

const SECTION_SELECTOR = '[data-section-type="featured-collection-with-media"]';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const wirePauseButton = (section, videoEl) => {
  const pause = section.querySelector('.video-pause-floating');
  if (!pause || pause.dataset.fcwmWired === 'true') return;
  pause.dataset.fcwmWired = 'true';

  pause.addEventListener('click', (event) => {
    event.preventDefault();
    if (videoEl.paused) {
      videoEl.play();
      pause.dataset.isPlaying = 'true';
    } else {
      videoEl.pause();
      pause.dataset.isPlaying = 'false';
    }
  });
};

const injectVideo = (section, wrapper) => {
  if (wrapper.querySelector('video')) return;

  const template = wrapper.querySelector('template[data-fcwm-video-template]');
  if (!template) return;

  const clone = template.content.cloneNode(true);
  const videoEl = clone.querySelector('video');
  const poster = wrapper.querySelector('[data-fcwm-video-poster]');

  if (videoEl) {
    // The Liquid video_tag was rendered with preload="none" and no autoplay so
    // the .mp4 isn't fetched at parse time. Re-enable muted/inline here; only
    // auto-play when the visitor hasn't asked to reduce motion.
    videoEl.muted = true;
    videoEl.setAttribute('playsinline', '');
    if (!reduceMotion) videoEl.setAttribute('autoplay', '');

    // Fade the poster out once the video can paint its first frame.
    if (poster) {
      videoEl.addEventListener(
        'loadeddata',
        () => { poster.style.opacity = '0'; },
        { once: true }
      );
    }
  }

  if (poster) {
    poster.before(clone);
  } else {
    wrapper.append(clone);
  }

  if (!videoEl) return;

  wirePauseButton(section, videoEl);

  if (reduceMotion) {
    const pauseBtn = section.querySelector('.video-pause-floating');
    if (pauseBtn) pauseBtn.dataset.isPlaying = 'false';
  } else {
    videoEl.play()?.catch(() => {});
  }
};

const initSection = (section) => {
  if (!section || section.dataset.fcwmVideoInit === 'true') return;
  section.dataset.fcwmVideoInit = 'true';

  const loadAll = () => {
    section
      .querySelectorAll('.featured-collection-with-media__video-wrapper')
      .forEach((wrapper) => injectVideo(section, wrapper));
  };

  // Defer the fetch until the section is near the viewport. The 200px rootMargin
  // starts it slightly before it scrolls in, so the visitor sees video, not poster.
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadAll();
          observer.disconnect();
        });
      },
      { rootMargin: '200px' }
    );
    observer.observe(section);
  } else {
    loadAll();
  }
};

const initAll = (root = document) => {
  root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
};

initAll();

// Theme editor: (re-)initialise sections added or reloaded in the customizer.
document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});
